// RefreshDialogs.tsx — verdict dialogs + scan/build progress overlays for
// product refresh (KAN-1464, Task 6).
//
// PRESENTATION-ONLY: no API calls. Every mutating action is a callback prop;
// Task 7 wires these to the productRefresh service module (scan.ts, commit.ts,
// drastic.ts). Content is ported from UpdateProductModal.tsx's dialogs:
// - MinorEvolutionDialog: ~2337-2536 (added personas/features/user stories)
// - RefinementOnlyDialog: `showRefinementOnlyDialog` block (~2649-2732)
// - DrasticChangeDialog: ~2071-2335 (new-product-name prompt)
// - NoChangesDialog: `showNoChangesDialog` block (~2593-2647)
// Styled with np-v6 tokens; these components render inside the `.np-v6` scope.

import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

// ---------------------------------------------------------------------------
// Shared safe-extraction helpers (mirror commit.ts's hasAddedContent-style
// optional chaining). `result` is `unknown` by contract (Task 1's ScanOutcome
// carries the raw comparison_result payload through untyped) so every read
// here is defensive.
// ---------------------------------------------------------------------------

interface AddedPersona {
    name?: string;
    added_reason?: string;
}

interface AddedUserStory {
    name?: string;
    source?: string;
}

interface MergedFeature {
    title?: string;
    source?: string;
    user_stories?: AddedUserStory[];
}

function getAddedPersonas(result: unknown): AddedPersona[] {
    const personas = (result as any)?.merged_personas?.added_personas;
    return Array.isArray(personas) ? personas : [];
}

function getAddedFeatures(result: unknown): MergedFeature[] {
    const features = (result as any)?.merged_features?.features;
    if (!Array.isArray(features)) return [];
    return features.filter((f: MergedFeature) => f?.source === "ADDED");
}

function getAddedUserStoriesByFeature(result: unknown): Array<{ featureTitle: string; story: AddedUserStory }> {
    const features = (result as any)?.merged_features?.features;
    if (!Array.isArray(features)) return [];
    const rows: Array<{ featureTitle: string; story: AddedUserStory }> = [];
    for (const feature of features as MergedFeature[]) {
        const stories = Array.isArray(feature?.user_stories) ? feature.user_stories : [];
        for (const story of stories) {
            if (story?.source === "ADDED") {
                rows.push({ featureTitle: feature?.title ?? "", story });
            }
        }
    }
    return rows;
}

function getSummary(result: unknown): string | undefined {
    return typeof (result as any)?.summary === "string" ? (result as any).summary : undefined;
}

function getGateReasoning(result: unknown): string | undefined {
    return typeof (result as any)?.gate_reasoning === "string" ? (result as any).gate_reasoning : undefined;
}

// ---------------------------------------------------------------------------
// np-v6 styling helpers for MUI Dialog surfaces (match RefreshBanner /
// NewProductScreen aesthetic rather than raw MUI defaults).
// ---------------------------------------------------------------------------

const dialogPaperSx = {
    borderRadius: 2,
    bgcolor: "var(--bg-paper)",
    color: "var(--fg1)"
};

// MUI Dialog portals to document.body, OUTSIDE the screen's `.np-v6` scope
// where tokens.css defines every --bg-paper/--fg*/--np-* variable. The Paper
// must carry the scope class itself or all var() styles resolve as invalid
// (transparent surface, unreadable text).
const dialogPaperProps = { className: "np-v6", sx: dialogPaperSx };

const dialogTitleSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    borderBottom: "1px solid var(--divider)"
};

// ---------------------------------------------------------------------------
// MinorEvolutionDialog
// ---------------------------------------------------------------------------

export function MinorEvolutionDialog(props: {
    open: boolean;
    result: unknown;
    onConfirm: () => void;
    onCancel: () => void;
}): JSX.Element {
    const { open, result, onConfirm, onCancel } = props;

    const addedPersonas = getAddedPersonas(result);
    const addedFeatures = getAddedFeatures(result);
    const addedUserStories = getAddedUserStoriesByFeature(result);
    const hasAnyAdditions = addedPersonas.length > 0 || addedFeatures.length > 0 || addedUserStories.length > 0;

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth PaperProps={dialogPaperProps}>
            <DialogTitle sx={{ ...dialogTitleSx, color: "var(--np-success)" }}>
                <CheckCircleIcon sx={{ color: "var(--np-success)" }} />
                Modifications Allowed Within Product Scope
            </DialogTitle>
            <DialogContent sx={{ overflowY: "auto" }}>
                {addedPersonas.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: "var(--fg1)" }}>
                            Personas Added:
                        </Typography>
                        <List dense>
                            {addedPersonas.map((persona, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary={persona.name}
                                        secondary={persona.added_reason}
                                        primaryTypographyProps={{ fontWeight: 500 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {addedFeatures.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: "var(--fg1)" }}>
                            Features Added:
                        </Typography>
                        <List dense>
                            {addedFeatures.map((feature, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText primary={feature.title} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {addedUserStories.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: "var(--fg1)" }}>
                            User Stories Added:
                        </Typography>
                        <List dense>
                            {addedUserStories.map((row, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary={`${row.featureTitle} - ${row.story.name}`}
                                        primaryTypographyProps={{ variant: "body2" }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {!hasAnyAdditions && (
                    <Typography variant="body1" sx={{ color: "var(--fg3)" }}>
                        No new personas, features, or user stories were added in this update.
                    </Typography>
                )}
            </DialogContent>
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    bgcolor: "var(--bg-subtle)",
                    borderTop: "1px solid var(--divider)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Typography variant="body2" sx={{ color: "var(--fg3)" }}>
                    Do you wish to continue with the product updates?
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button variant="outlined" onClick={onCancel}>
                        No
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onConfirm}
                        sx={{ bgcolor: "var(--np-brand)", "&:hover": { bgcolor: "var(--np-brand-hover)" } }}
                    >
                        Yes
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// RefinementOnlyDialog
// ---------------------------------------------------------------------------

export function RefinementOnlyDialog(props: {
    open: boolean;
    result: unknown;
    onConfirm: () => void;
    onCancel: () => void;
}): JSX.Element {
    const { open, result, onConfirm, onCancel } = props;

    const addedPersonas = getAddedPersonas(result);
    const gateReasoning =
        getGateReasoning(result) || "Your changes are refinements to existing features. No new features or user stories are needed.";

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
            <DialogTitle sx={{ ...dialogTitleSx, color: "var(--np-success)" }}>
                <CheckCircleIcon sx={{ color: "var(--np-success)" }} />
                Refinements Applied
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2, color: "var(--fg2)" }}>
                    {gateReasoning}
                </Typography>

                {addedPersonas.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "var(--fg1)" }}>
                            Personas Added:
                        </Typography>
                        <List dense>
                            {addedPersonas.map((persona, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary={persona.name}
                                        secondary={persona.added_reason}
                                        primaryTypographyProps={{ fontWeight: 500 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onCancel}>
                    No
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    sx={{ bgcolor: "var(--np-brand)", "&:hover": { bgcolor: "var(--np-brand-hover)" } }}
                >
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// NoChangesDialog
// ---------------------------------------------------------------------------

export function NoChangesDialog(props: { open: boolean; onClose: () => void }): JSX.Element {
    const { open, onClose } = props;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
            <DialogTitle sx={{ ...dialogTitleSx, color: "var(--np-success)" }}>
                <CheckCircleIcon sx={{ color: "var(--np-success)" }} />
                No Changes Detected
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2, color: "var(--fg2)" }}>
                    No answer changes were detected. Your specifications remain unchanged.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{ bgcolor: "var(--np-brand)", "&:hover": { bgcolor: "var(--np-brand-hover)" } }}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// DrasticChangeDialog
// ---------------------------------------------------------------------------

export function DrasticChangeDialog(props: {
    open: boolean;
    onConfirm: (newProductName: string) => void;
    onCancel: () => void;
}): JSX.Element {
    const { open, onConfirm, onCancel } = props;
    const [newProductName, setNewProductName] = useState("");

    // Reset the typed name once the dialog closes (cancel or confirm) so a
    // reopen never shows a stale value from the previous scan.
    useEffect(() => {
        if (!open) setNewProductName("");
    }, [open]);

    const trimmedName = newProductName.trim();
    const canConfirm = trimmedName.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
            PaperProps={dialogPaperProps}
        >
            <DialogTitle sx={{ ...dialogTitleSx, color: "var(--np-warn)" }}>
                <WarningIcon sx={{ color: "var(--np-warn)" }} />
                Warning!
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2, whiteSpace: "pre-wrap", color: "var(--fg2)" }}>
                    Your recent changes require creating a new product, as they can&apos;t be applied to the current
                    one. If you proceed, the existing product will remain unchanged and a new one will be generated.
                </Typography>
                <TextField
                    label="New Product Name"
                    value={newProductName}
                    onChange={e => setNewProductName(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ mt: 2 }}
                />
                <Typography variant="body1" sx={{ mt: 2, color: "var(--fg2)" }}>
                    Would you like to continue?
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onCancel}>
                    No
                </Button>
                <Button
                    variant="contained"
                    disabled={!canConfirm}
                    onClick={() => onConfirm(trimmedName)}
                    sx={{ bgcolor: "var(--np-brand)", "&:hover": { bgcolor: "var(--np-brand-hover)" } }}
                >
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Progress overlays — indeterminate only. Per Task 3, onProgress supplies
// only (phase, message); there are NO numeric progress values, so these
// render a spinner + phase label + message rather than a percent bar.
// ---------------------------------------------------------------------------

function ProgressOverlay(props: { open: boolean; phase: string; message?: string; title: string }): JSX.Element {
    const { open, phase, message, title } = props;

    return (
        <Dialog
            open={open}
            onClose={() => undefined}
            disableEscapeKeyDown
            maxWidth="xs"
            fullWidth
            PaperProps={dialogPaperProps}
        >
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                    <CircularProgress sx={{ color: "var(--np-brand)" }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--fg1)" }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "var(--fg3)", textTransform: "capitalize" }}>
                        {phase}
                    </Typography>
                    {message && (
                        <Typography variant="body2" sx={{ color: "var(--fg2)", textAlign: "center" }}>
                            {message}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export function ScanProgressOverlay(props: { open: boolean; phase: string; message?: string }): JSX.Element {
    return <ProgressOverlay {...props} title="Scanning your changes" />;
}

export function BuildProgressOverlay(props: { open: boolean; phase: string; message?: string }): JSX.Element {
    return <ProgressOverlay {...props} title="Building your new product" />;
}
